// Ark Responses adapter for text, structured tools, and uploaded video.
import * as fs from "node:fs";
import * as path from "node:path";
import { File } from "node:buffer";
import { Agent, fetch, FormData } from "undici";

import {
  LlmTransportError,
  type JsonSchema,
  type LlmAdapter,
  type TextGenerationRequest,
  type TextGenerationResponse,
  type ToolGenerationRequest,
  type ToolGenerationResponse,
  type TokenUsage,
  type VideoGenerationResponse,
} from "../../llm/types.js";
import { httpError, httpStatusOf, networkError } from "./failure-mapping.js";
import { addTokenUsage, withTransientRetry } from "./retry.js";

// Ark responses payload — the subset we read. output[] holds reasoning + message
// items; message.content[].text (type output_text) is the answer. status is
// "completed" | "incomplete" (incomplete_details.reason tells why).
export interface ArkResponse {
  status?: string;
  incomplete_details?: { reason?: string };
  output?: Array<{ type?: string; content?: Array<{ type?: string; text?: string }> }>;
  usage?: { input_tokens?: number; output_tokens?: number };
}

export interface ArkFile {
  id?: string;
  status?: string; // processing | active | failed
}

export interface ArkClient {
  uploadFile(params: { filePath: string; mimeType: string; fps: number }): Promise<ArkFile>;
  getFile(params: { id: string }): Promise<ArkFile>;
  createResponse(params: Record<string, unknown>, signal?: AbortSignal): Promise<ArkResponse>;
  deleteFile(params: { id: string }): Promise<void>;
}

const POLL_INTERVAL_MS = 2000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Map an SDK/HTTP error to a transport error with the shared retry policy: retry
// only transient network/timeout/408/429/5xx.
function toArkError(error: unknown, signal: AbortSignal | undefined, what: string, model: string): LlmTransportError {
  if (error instanceof LlmTransportError) return error;
  const message = error instanceof Error ? error.message : String(error);
  const status = httpStatusOf(error);
  const context = { provider: "doubao", model, operation: what };
  if (status !== null) {
    return httpError(status, `Doubao ${what} failed.`, message.slice(0, 500), context);
  }
  return networkError(error, signal, `Doubao ${what} failed.`, message.slice(0, 500), context);
}

function arkUsage(u: ArkResponse["usage"]): TokenUsage {
  if (!u) return {};
  const num = (v: unknown): number | undefined => (typeof v === "number" ? v : undefined);
  return { inputTokens: num(u.input_tokens), outputTokens: num(u.output_tokens) };
}

// Concatenate every output_text across message items (reasoning items carry no
// output_text, so they are naturally skipped).
function extractText(res: ArkResponse): string {
  const parts: string[] = [];
  for (const item of res.output ?? []) {
    if (item.type !== "message") continue;
    for (const c of item.content ?? []) {
      if (c.type === "output_text" && typeof c.text === "string") parts.push(c.text);
    }
  }
  return parts.join("").trim();
}

export interface DoubaoRunRequest {
  localPath: string;
  localMime: string;
  system?: string;
  user: string;
  fps: number;
  maxOutputTokens: number;
  responseJsonSchema?: JsonSchema;
  signal?: AbortSignal;
}

export class DoubaoModel implements LlmAdapter {
  private readonly client: ArkClient;

  constructor(
    private readonly model: string,
    private readonly timeoutMs: number,
    private readonly maxAttempts: number,
    apiKey: string,
    baseUrl: string,
    client?: ArkClient,
  ) {
    this.client = client ?? createArkClient(apiKey, baseUrl, timeoutMs);
  }

  // upload local transcode → poll ACTIVE → respond → best-effort delete.
  async run(request: DoubaoRunRequest): Promise<VideoGenerationResponse> {
    const { value: file } = await withTransientRetry(this.maxAttempts, async () => {
      try {
        return await this.client.uploadFile({ filePath: request.localPath, mimeType: request.localMime, fps: request.fps });
      } catch (error) {
        throw toArkError(error, request.signal, "upload", this.model);
      }
    });
    if (!file.id) throw new LlmTransportError("invalid_response", "Doubao upload returned no file id.");
    const active = await this.pollActive(file.id, request.signal);
    try {
      return await this.generateOnce(active, request);
    } finally {
      await this.client.deleteFile({ id: active }).catch(() => {}); // cleanup is best-effort
    }
  }

  private async pollActive(id: string, signal?: AbortSignal): Promise<string> {
    const deadline = Date.now() + this.timeoutMs;
    let status = "processing";
    while (status === "processing" || status === "") {
      if (signal?.aborted) {
        throw new LlmTransportError("cancelled", "Doubao upload aborted.", {
          context: { provider: "doubao", model: this.model, operation: "file_poll" },
        });
      }
      if (Date.now() >= deadline) {
        throw new LlmTransportError("timeout", "Doubao file processing timed out.", {
          retryable: true,
          classification: { failure_type: "timeout", failure_code: "media_processing_timeout" },
          context: { provider: "doubao", model: this.model, operation: "file_poll" },
        });
      }
      await delay(POLL_INTERVAL_MS);
      let file: ArkFile;
      try {
        file = await this.client.getFile({ id });
      } catch (error) {
        throw toArkError(error, signal, "file_poll", this.model);
      }
      status = (file.status ?? "").toLowerCase();
    }
    if (status === "failed" || status === "error") {
      throw new LlmTransportError("invalid_response", "Doubao file processing failed.", {
        classification: { failure_type: "media", failure_code: "media_processing_failed" },
        context: { provider: "doubao", model: this.model, operation: "file_poll" },
      });
    }
    return id;
  }

  private async generateOnce(fileId: string, request: DoubaoRunRequest): Promise<VideoGenerationResponse> {
    const params: Record<string, unknown> = {
      model: this.model,
      max_output_tokens: request.maxOutputTokens,
      input: [{ role: "user", content: [
        { type: "input_video", file_id: fileId },
        { type: "input_text", text: request.user },
      ] }],
    };
    if (request.system?.trim()) params["instructions"] = request.system;
    if (request.responseJsonSchema) {
      params["text"] = { format: { type: "json_schema", name: "video_serial", schema: request.responseJsonSchema } };
    }
    return this.generateResponse(params, request.signal, Boolean(request.responseJsonSchema));
  }

  async generateText(request: TextGenerationRequest): Promise<TextGenerationResponse> {
    const params = this.textParams(request);
    if (request.responseFormat) params["text"] = { format: { type: request.responseFormat } };
    return this.generateResponse(params, request.signal, Boolean(request.responseFormat));
  }

  async generateTool(request: ToolGenerationRequest): Promise<ToolGenerationResponse> {
    const params = this.textParams(request);
    params["text"] = { format: {
      type: "json_schema", name: request.tool.name,
      description: request.tool.description, schema: request.tool.inputSchema,
    } };
    const response = await this.generateResponse(params, request.signal, true);
    return { input: JSON.parse(response.text), finishReason: response.finishReason,
      attempts: response.attempts, usage: response.usage, raw: response.raw };
  }

  private textParams(request: TextGenerationRequest): Record<string, unknown> {
    return {
      model: this.model, max_output_tokens: request.maxOutputTokens,
      instructions: request.system,
      input: [{ role: "user", content: [{ type: "input_text", text: request.user }] }],
    };
  }

  private async generateResponse(
    params: Record<string, unknown>, signal: AbortSignal | undefined, structured: boolean,
  ): Promise<TextGenerationResponse> {
    const { value, attempts, failedUsage } = await withTransientRetry(this.maxAttempts, async () => {
      let res: ArkResponse;
      try {
        res = await this.client.createResponse(params, signal);
      } catch (error) {
        throw toArkError(error, signal, "generate", this.model);
      }
      const status = (res.status ?? "").toLowerCase();
      const usage = arkUsage(res.usage);
      if (status === "incomplete") {
        const reason = res.incomplete_details?.reason ?? "unknown";
        if (reason === "max_output_tokens") throw new LlmTransportError("truncated", "Doubao output was truncated.", { usage });
        throw new LlmTransportError("content_filtered", `Doubao response incomplete (reason=${reason}).`, { usage });
      }
      const text = extractText(res);
      if (!text) throw new LlmTransportError("empty_response", "Doubao returned an empty response.", { retryable: true, usage });
      if (structured) {
        try {
          JSON.parse(text);
        } catch {
          throw new LlmTransportError("invalid_response", "Doubao returned malformed JSON for a structured response.", {
            retryable: true,
            usage,
            context: { provider: "doubao", model: this.model, operation: "generate" },
          });
        }
      }
      return { text, usage, raw: res };
    });
    return { text: value.text, finishReason: "STOP", attempts, usage: addTokenUsage(failedUsage, value.usage), raw: value.raw };
  }
}

// Real HTTP client over the Ark REST API using global fetch (Node 18+). No SDK
// dependency — the surface is tiny (4 calls) and multipart upload is trivial.
class HttpError extends Error {
  constructor(readonly status: number, message: string) { super(message); this.name = "HttpError"; }
}

function createArkClient(apiKey: string, baseUrl: string, timeoutMs: number): ArkClient {
  const root = baseUrl.replace(/\/$/, "");
  const auth = { Authorization: `Bearer ${apiKey}` };
  // Node's global fetch uses Undici's 5-minute response-header timeout even
  // when the AbortSignal allows a longer model request. Ark video responses
  // can legitimately take longer, so keep the transport deadline aligned with
  // the provider timeout instead of failing every retry at exactly 5 minutes.
  const dispatcher = new Agent({ headersTimeout: timeoutMs, bodyTimeout: timeoutMs });

  function signalFor(extra?: AbortSignal): AbortSignal {
    const timeout = AbortSignal.timeout(timeoutMs);
    return extra ? AbortSignal.any([extra, timeout]) : timeout;
  }

  async function readError(res: Response): Promise<never> {
    const body = await res.text().catch(() => "");
    throw new HttpError(res.status, body.slice(0, 500));
  }

  return {
    async uploadFile({ filePath, mimeType, fps }) {
      const form = new FormData();
      const bytes = fs.readFileSync(filePath);
      form.append("file", new File([bytes], path.basename(filePath), { type: mimeType }));
      form.append("purpose", "user_data");
      form.append("preprocess_configs[video][fps]", String(fps));
      const res = await fetch(`${root}/files`, { method: "POST", headers: auth, body: form, signal: signalFor(), dispatcher });
      if (!res.ok) await readError(res);
      return (await res.json()) as ArkFile;
    },
    async getFile({ id }) {
      const res = await fetch(`${root}/files/${id}`, { headers: auth, signal: signalFor(), dispatcher });
      if (!res.ok) await readError(res);
      return (await res.json()) as ArkFile;
    },
    async createResponse(params, signal) {
      const res = await fetch(`${root}/responses`, {
        method: "POST",
        headers: { ...auth, "Content-Type": "application/json" },
        body: JSON.stringify(params),
        signal: signalFor(signal),
        dispatcher,
      });
      if (!res.ok) await readError(res);
      return (await res.json()) as ArkResponse;
    },
    async deleteFile({ id }) {
      const res = await fetch(`${root}/files/${id}`, { method: "DELETE", headers: auth, signal: signalFor(), dispatcher });
      if (!res.ok) await readError(res);
    },
  };
}
